import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import RouterPropTypes from 'react-router-prop-types';
import { Alert, Button, Container } from 'reactstrap';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { size, uniqueId } from 'lodash';

import Page from '../../components/layout/Page';
import RouteService from '../../services/RouteService';
import SidebarLayout from '../../components/layout/SidebarLayout';
import CanonicalLink from '../../components/head/CanonicalLink';
import ScrollReset from '../../components/common/ScrollReset';
import MyAccountNavigation from '../../components/my-account/MyAccountNavigation';
import Paths from '../../types/Paths';
import MyAccountOverview from '../../components/my-account/MyAccountOverview';
import MyAccountOrderList from '../../components/my-account/MyAccountOrderList';
import MyAccountOrder from '../../components/my-account/MyAccountOrder';
import MyAccountInfo from '../../components/my-account/MyAccountInfo';
import MyAccountChangePassword from '../../components/my-account/MyAccountChangePassword';
import { modelOf } from '../../prop-types';
import AccountStore from '../../store/AccountStore';
import AccountModalTab from '../../types/AccountModalTab';
import globalTranslations from '../../i18n/globalTranslations';
import AccountModalLink from '../../components/account/AccountModalLink';
import OrderStore from '../../store/OrderStore';
import MyAccountAddressList from '../../components/my-account/MyAccountAddressList';
import MyAccountAddressNew from '../../components/my-account/MyAccountAddressNew';
import MyAccountAddress from '../../components/my-account/MyAccountAddress';
import { paramsToQueryIdentifier } from '../../util/query';
import { parse } from '../../util/queryString';
import SEOTitle from '../../components/head/SEOTitle';
import MyAccountUserList from '../../components/my-account/MyAccountUserList';
import MyAccountUserNew from '../../components/my-account/MyAccountUserNew';
import MyAccountUser from '../../components/my-account/MyAccountUser';
import CustomerUserStore from '../../store/CustomerUserStore';
import MyAccountPurchasesList from '../../components/my-account/MyAccountPurchasesList';
import MyAccountCreditAccount from '../../components/my-account/MyAccountCreditAccount';
import CustomerProductsPage from '../CustomerProductsPage';
import MyAccountThankYouPage from '../MyAccountThankYouPage';
import BalanceUserAccountOverview from '../../components/my-account/BalanceUser/BalanceUserAccountOverview';
import BalanceUserBalanceAccount from '../../components/my-account/BalanceUser/BalanceUserBalanceAccount';
import BalanceSubUserAccountOverview from '../../components/my-account/BalanceUser/BalanceSubUserAccountOverview';
import MyAccountPaymentTokenList from '../../components/my-account/MyAccountPaymentTokenList';
import PaymentTokenStore from '../../store/PaymentTokenStore';
import MyAccountRecurringOrderList from '../../components/my-account/MyAccountRecurringOrderList';
import ConfigStore from '../../store/ConfigStore';
import MyAccountRecurringOrder from '../../components/my-account/MyAccountRecurringOrder';
import CommonPage from '../../types/CommonPage';

const createRoute = (path, component) => {
  return { path, component };
};

const ROUTES = [
  createRoute(Paths.MyAccount, MyAccountOverview),
  createRoute(Paths.MyAccountInfo, MyAccountInfo),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountAddressList, MyAccountAddressList),
  createRoute(Paths.MyAccountAddressNew, MyAccountAddressNew),
  createRoute(Paths.MyAccountAddress, MyAccountAddress)
];

const TOKEN_ROUTES = [
  createRoute(Paths.MyAccountPaymentTokenList, MyAccountPaymentTokenList)
];

const NON_BALANCE_ROUTES = [
  createRoute(Paths.MyAccountOrderList, MyAccountOrderList)
];

const BALANCE_ROUTES = [
  createRoute(Paths.MyAccountBalanceUser, BalanceUserAccountOverview),
  createRoute(Paths.MyAccountBalanceUserAccountInfo, MyAccountInfo),
  createRoute(
    Paths.MyAccountBalanceUserPaymentHistory,
    BalanceUserBalanceAccount
  ),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountUserNew, MyAccountUserNew),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage)
];

const BALANCE_USER_ROUTES = [
  createRoute(Paths.MyAccountBalanceSubUser, BalanceSubUserAccountOverview)
];

const RETAILER_ROUTES = [
  createRoute(Paths.CustomerProducts, CustomerProductsPage),
  createRoute(Paths.MyAccountUserList, MyAccountUserList),
  createRoute(Paths.MyAccountUserNew, MyAccountUserNew),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountPurchasesList, MyAccountPurchasesList),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage)
];

const CUSTOMER_USER_ROUTES = [
  createRoute(Paths.MyAccount, MyAccountOverview),
  createRoute(Paths.MyAccountChangePassword, MyAccountChangePassword),
  createRoute(Paths.MyAccountOrderList, MyAccountOrderList),
  createRoute(Paths.CustomerProducts, CustomerProductsPage),
  createRoute(Paths.MyAccountUser, MyAccountUser),
  createRoute(Paths.MyAccountThankYouPage, MyAccountThankYouPage)
];

const RETAILER_LIMITED_ROUTES = [
  createRoute(Paths.MyAccountCreditAccount, MyAccountCreditAccount)
];

const RECURRING_ORDER_ROUTES = [
  createRoute(Paths.MyAccountRecurringOrderList, MyAccountRecurringOrderList),
  createRoute(Paths.MyAccountRecurringOrder, MyAccountRecurringOrder)
];

const ORDER_SEARCH_PARAMS = {};

@observer
class MyAccountPage extends Component {
  state = {
    creditAccess: false,
    tokenAccess: false
  };

  componentDidMount() {
    const { accountStore, orderStore, customerUserStore } = this.props;

    if (accountStore.loggedIn && !accountStore.isBalanceBuyer) {
      orderStore.loadOrders(ORDER_SEARCH_PARAMS);
    }

    if (accountStore.isRetailer) {
      customerUserStore
        .loadCreditAccount()
        .then(() => {
          this.setState({
            creditAccess: true
          });
        })
        .catch(() => {
          this.setState({
            creditAccess: false
          });
        });

      if (!accountStore.isCustomerUser) {
        customerUserStore.loadUsers();
      }
    }

    // Check Payment card status before creating route
    if (accountStore.loggedIn) {
      const { paymentTokenStore } = this.props;
      paymentTokenStore
        .loadTokens()
        .then(() => {
          this.setState({
            tokenAccess: true
          });
        })
        .catch(() => {
          this.setState({
            tokenAccess: false
          });
        });
    }
  }

  getOrderCount() {
    const { orderStore } = this.props;

    const orderList = orderStore.orderQueryResults.get(
      paramsToQueryIdentifier(ORDER_SEARCH_PARAMS)
    );
    return orderList ? orderList.total : 0;
  }

  getUserCount() {
    const { customerUserStore, accountStore } = this.props;

    return accountStore.isBalanceBuyer
      ? size(customerUserStore.balanceUsers)
      : size(customerUserStore.users);
  }

  getCurrentOrderAuthenticationCode = () => {
    const query = parse(this.props.location.search);
    return query.code;
  };

  hasCreditAccountAccess = () => this.state.creditAccess;

  hasTokenAccess = () => this.state.tokenAccess;

  getAccountRoutes() {
    const { accountStore, configStore } = this.props;
    const params = parse(this.props.location.search);

    let accountRoutes = ROUTES;

    if (accountStore.isCustomerUser) {
      accountRoutes = CUSTOMER_USER_ROUTES;

      if (accountStore.isUserAllowedAddressModification) {
        accountRoutes = accountRoutes.concat([
          createRoute(Paths.MyAccountAddressList, MyAccountAddressList),
          createRoute(Paths.MyAccountAddressNew, MyAccountAddressNew),
          createRoute(Paths.MyAccountAddress, MyAccountAddress)
        ]);
      }
    } else {
      accountRoutes = accountRoutes.concat(NON_BALANCE_ROUTES);
    }

    if (accountStore.isRetailer) {
      if (!accountStore.isCustomerUser) {
        accountRoutes = accountRoutes.concat(RETAILER_ROUTES);
      }

      if (this.hasCreditAccountAccess()) {
        accountRoutes = accountRoutes.concat(RETAILER_LIMITED_ROUTES);
      }
    }

    if (configStore.recurringOrder.enabled) {
      accountRoutes = accountRoutes.concat(RECURRING_ORDER_ROUTES);
    }

    if (accountStore.isBalanceBuyer) {
      accountRoutes = BALANCE_ROUTES;
    }

    if (accountStore.isBalanceUser) {
      accountRoutes = BALANCE_USER_ROUTES;
    }

    if (this.hasTokenAccess() || !!params.tokenizationSuccess) {
      accountRoutes = accountRoutes.concat(TOKEN_ROUTES);
    }

    return accountRoutes;
  }

  render() {
    const { match, accountStore, routeService } = this.props;
    const code = this.getCurrentOrderAuthenticationCode();
    const allowOrderCloning = accountStore.loggedIn;

    return (
      <Page name={CommonPage.MY_ACCOUNT} className="MyAccountPage">
        <ScrollReset key={match.params.id} />
        <CanonicalLink path={routeService.getPath(Paths.MyAccount)} />
        <Container className="MyAccountPage__container">
          <SidebarLayout
            forceSidebar={true}
            sidebar={
              <MyAccountNavigation
                routes={this.getAccountRoutes()}
                orderCount={this.getOrderCount()}
                userCount={this.getUserCount()}
              />
            }
            content={
              <Fragment>
                <Switch>
                  {accountStore.loggedIn &&
                    this.getAccountRoutes().map((route) => {
                      return (
                        <Route
                          key={uniqueId(route.path)}
                          exact
                          path={routeService.getPath(route.path)}
                          component={route.component}
                        />
                      );
                    })}
                  {(accountStore.loggedIn || code) && (
                    <Route
                      exact
                      path={routeService.getPath(Paths.MyAccountOrder)}
                      render={(props) => (
                        <MyAccountOrder
                          {...props}
                          code={code}
                          allowClone={allowOrderCloning}
                        />
                      )}
                    />
                  )}
                  {!accountStore.loggedIn && (
                    <Route
                      render={() => (
                        <Fragment>
                          <SEOTitle title={globalTranslations.myAccountTitle} />
                          <Alert
                            className="MyAccountPage__container-info"
                            color="warning"
                          >
                            <FormattedMessage
                              id="myAccount.loginInfo"
                              defaultMessage="By logging in you can see your order history and modify your personal information."
                            />
                          </Alert>
                          <AccountModalLink tab={AccountModalTab.LOGIN}>
                            <Button tag="span" color="primary">
                              <FormattedMessage
                                {...globalTranslations.logInSentence}
                              />
                            </Button>
                          </AccountModalLink>
                        </Fragment>
                      )}
                    />
                  )}
                  <Redirect to="/" />
                </Switch>
              </Fragment>
            }
          />
        </Container>
      </Page>
    );
  }
}

MyAccountPage.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  paymentTokenStore: modelOf(PaymentTokenStore).isRequired,
  location: RouterPropTypes.location.isRequired,
  match: RouterPropTypes.match.isRequired
};

export default withRouter(
  inject(
    'routeService',
    'accountStore',
    'configStore',
    'orderStore',
    'customerUserStore',
    'paymentTokenStore'
  )(MyAccountPage)
);



// WEBPACK FOOTER //
// ./src/pages/MyAccountPage/index.jsx